import React from "react";
import Link from "../audience/ScientificLink";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  ClipboardCheck,
  PackageCheck,
  Workflow,
  UsersRound,
} from "lucide-react";

const engagementPath = [
  {
    step: "01",
    title: "Assess the workflow",
    description:
      "We trace the method, instruments, software, data, owners, and failure points with the people who run the work.",
  },
  {
    step: "02",
    title: "Scope the solution",
    description:
      "We define the smallest useful build, its dependencies, boundaries, and who will own it after delivery.",
  },
  {
    step: "03",
    title: "Agree on what must pass",
    description:
      "Before work begins, we write the acceptance criteria, required evidence, test conditions, and sign-off owner.",
  },
  {
    step: "04",
    title: "Build to acceptance",
    description:
      "We build, install, and test the agreed scope, then hand it over or keep supporting it under the ownership model you choose.",
  },
];

const engagementModels = [
  {
    id: "focused-installation",
    number: "01",
    name: "Focused build & handoff",
    ownership: "Your team runs it",
    description:
      "Fix one bounded part of an otherwise working workflow. We build and install the missing method, driver, or software component, test it against the agreed acceptance criteria on the named system, and hand it to your team.",
    bestFor:
      "Teams that know what they need and have an internal owner ready to support it after handoff.",
    examples: [
      "Method development, transfer, or repair",
      "Instrument drivers and device adapters",
      "Liquid classes and targeted automation fixes",
      "Small workflow utilities or connectors",
    ],
    ownershipDetail:
      "We hand over the tested build, editable files, known limits, and runbook. After the agreed stabilization period, your team owns routine operation, updates, and maintenance.",
    cta: "Scope a focused build",
    icon: PackageCheck,
  },
  {
    id: "managed-integration",
    number: "02",
    name: "Managed integration",
    ownership: "We maintain the integrations",
    description:
      "Keep the lab systems you already have. Within a defined support scope, we build and maintain the integration layer as schemas, APIs, supported software versions, and workflows change.",
    bestFor:
      "Labs with functioning instruments and software that need reliable connections, orchestration, or ongoing software ownership.",
    examples: [
      "ELN, LIMS, instrument, and data connectors",
      "Scheduling and workflow orchestration",
      "Scientist-reviewed method drafting and testing",
      "Model evaluation and tuning on approved internal data",
    ],
    ownershipDetail:
      "Your team owns the lab, source systems, scientific decisions, credentials, and vendor relationships. Cornucopia maintains the integrations and services named in scope, with supported versions, monitoring, response windows, release cadence, and change allowance agreed up front.",
    cta: "Plan a managed integration",
    icon: Workflow,
    featured: true,
  },
  {
    id: "embedded-fde",
    number: "03",
    name: "Embedded forward-deployed engineering",
    ownership: "We lead delivery and support",
    description:
      "For teams that want one engineering lead across system design, vendor evaluation, commissioning, software, data integration, and post-go-live support under an agreed service plan.",
    bestFor:
      "New workcells, large cloud-lab installations, enterprise rollouts, and multi-system programs without one internal team available to own every interface.",
    examples: [
      "Workcell design, equipment requirements, vendor evaluation, and commissioning",
      "Control software, integrations, and agreed on-site support",
      "Schema validation, regression tests, and CI/CD release gates",
      "Enterprise, multi-site, and cloud-lab deployments",
    ],
    ownershipDetail:
      "Where scoped, Cornucopia coordinates integrated FAT/SAT and prepares the evidence; OEMs perform equipment-level tests and retain warranty, calibration, and hardware repair. Your team approves suppliers and purchase orders and retains facility, safety, IT, quality, scientific, and release authority. After go-live, we maintain the custom software and integrations, monitor agreed data contracts, run release checks, and provide on-site support for the agreed term and service window.",
    cta: "Discuss an embedded deployment",
    icon: UsersRound,
  },
];

const Pricing = () => {
  const reducedMotion = useReducedMotion();

  const fadeIn = (delay = 0) =>
    reducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-10% 0px" },
          transition: {
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1],
            delay,
          },
        };

  return (
    <main className="blue-handoff-canvas px-6 pb-20 pt-28 sm:pt-36">
      <section
        className="wide-shell-md mx-auto mb-16 max-w-4xl text-center sm:mb-20"
        aria-labelledby="how-we-work-heading"
      >
        <motion.h1
          id="how-we-work-heading"
          {...fadeIn(0.06)}
          className="mb-6 font-serif text-5xl font-normal leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-7xl min-[1920px]:text-[5rem]"
        >
          Start with the workflow.{" "}
          <span className="blue-handoff-display">
            Decide how it stays running.
          </span>
        </motion.h1>
      </section>

      <section
        className="wide-shell-lg mx-auto mb-24 max-w-6xl"
        aria-labelledby="assessment-heading"
      >
        <motion.div
          {...fadeIn(0.04)}
          className="mb-9 grid gap-8 border-l-2 border-primary bg-primary/[0.035] px-6 py-7 dark:bg-primary/[0.06] md:grid-cols-[0.72fr_1.28fr] md:items-end md:px-8"
        >
          <div>
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border border-primary/20 bg-white text-primary dark:border-primary/30 dark:bg-white/[0.04]">
              <ClipboardCheck className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2
              id="assessment-heading"
              className="font-serif text-4xl font-normal tracking-tight sm:text-5xl"
            >
              First, we assess the workflow.
            </h2>
          </div>
          <div>
            <p className="text-base leading-7 text-gray-700 dark:text-white/70">
              We work with the people running the workflow to find where time,
              data, or reliability is being lost. Their experience—and the
              constraints of the system—shape what we build and how we test it.
            </p>
            <Link
              to="/contact?solution=workflow-assessment&source=engagements"
              data-cta="engagement-contact"
              data-cta-variant="workflow-assessment"
              data-solution-id="workflow-assessment"
              className="group mt-6 inline-flex min-h-11 items-center gap-2 rounded-lg text-sm font-semibold text-gray-950 outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary dark:text-white dark:hover:text-primary"
            >
              Start with an assessment
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </div>
        </motion.div>

        <ol className="grid border-l border-t border-gray-200 dark:border-white/[0.08] sm:grid-cols-2 xl:grid-cols-4">
          {engagementPath.map((item, index) => (
            <motion.li
              key={item.step}
              {...fadeIn(0.05 + index * 0.05)}
              className="border-b border-r border-gray-200 p-6 dark:border-white/[0.08] sm:p-7"
            >
              <p className="mb-8 font-mono text-[11px] font-medium tracking-[0.14em] text-primary">
                {item.step}
              </p>
              <h3 className="mb-3 text-lg font-semibold tracking-tight">
                {item.title}
              </h3>
              <p className="text-sm leading-6 text-gray-600 dark:text-white/65">
                {item.description}
              </p>
            </motion.li>
          ))}
        </ol>
      </section>

      <section
        id="delivery-models"
        className="wide-shell-lg mx-auto mb-24 max-w-6xl scroll-mt-24"
        aria-labelledby="delivery-models-heading"
      >
        <motion.div {...fadeIn(0)} className="mb-10 max-w-3xl">
          <h2
            id="delivery-models-heading"
            className="mb-5 font-serif text-4xl font-normal tracking-tight sm:text-5xl"
          >
            Choose who owns the system after it works.
          </h2>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {engagementModels.map((model, index) => {
            const Icon = model.icon;

            return (
              <motion.article
                key={model.id}
                {...fadeIn(index * 0.07)}
                className={`flex h-full flex-col rounded-xl border p-7 sm:p-8 ${
                  model.featured
                    ? "border-primary/35 bg-primary/[0.035] dark:border-primary/35 dark:bg-primary/[0.07]"
                    : "border-gray-200 bg-gray-50/70 dark:border-white/[0.08] dark:bg-white/[0.025]"
                } ${
                  model.id === "embedded-fde"
                    ? "md:col-span-2 xl:col-span-1"
                    : ""
                }`}
              >
                <div className="mb-7 flex items-start justify-between gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary dark:border-white/[0.1] dark:bg-white/[0.04]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <span className="font-mono text-[11px] font-medium tracking-[0.14em] text-gray-500 dark:text-white/60">
                    {model.number}
                  </span>
                </div>

                <h3 className="mb-3 text-2xl font-semibold tracking-tight">
                  {model.name}
                </h3>
                <p className="mb-6 text-sm leading-6 text-gray-600 dark:text-white/65">
                  {model.description}
                </p>

                <div className="mb-6 border-y border-gray-200 py-4 dark:border-white/[0.08]">
                  <p className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-white/60">
                    Best for
                  </p>
                  <p className="text-sm leading-6 text-gray-700 dark:text-white/70">
                    {model.bestFor}
                  </p>
                </div>

                <div className="mb-7">
                  <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-white/60">
                    Typical scope
                  </p>
                  <ul className="space-y-2.5">
                    {model.examples.map((example) => (
                      <li
                        key={example}
                        className="flex items-start gap-2.5 text-sm leading-6 text-gray-700 dark:text-white/70"
                      >
                        <Check
                          className="mt-1 h-4 w-4 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        <span>{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-7 mt-auto border-l-2 border-primary/35 bg-white/70 px-4 py-4 dark:bg-white/[0.025]">
                  <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                    {model.ownership}
                  </p>
                  <p className="text-xs leading-5 text-gray-600 dark:text-white/65">
                    {model.ownershipDetail}
                  </p>
                </div>

                <Link
                  to={`/contact?solution=workflow-review&engagement=${model.id}&source=engagements`}
                  data-cta="engagement-contact"
                  data-solution-id="workflow-review"
                  data-engagement-tier={model.id}
                  className={`group inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#050507] ${
                    model.featured
                      ? "bg-primary text-white hover:bg-primary/90"
                      : "border border-gray-300 text-gray-700 hover:border-primary/50 hover:text-primary dark:border-white/[0.14] dark:text-white/70 dark:hover:border-primary/50 dark:hover:text-primary"
                  }`}
                >
                  {model.cta}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </Link>
              </motion.article>
            );
          })}
        </div>
      </section>

      <motion.section
        {...fadeIn(0.04)}
        className="wide-shell-md mx-auto max-w-4xl rounded-xl border border-gray-200 bg-gray-50 px-7 py-10 text-center dark:border-white/[0.08] dark:bg-white/[0.025] sm:px-10"
        aria-labelledby="engagement-cta-heading"
      >
        <h2
          id="engagement-cta-heading"
          className="mb-7 text-2xl font-semibold tracking-tight md:text-3xl"
        >
          Not sure which model fits?
        </h2>
        <Link
          to="/contact?solution=workflow-assessment&source=engagements"
          data-cta="discuss-project"
          data-solution-id="workflow-assessment"
          className="group inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white outline-none transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#050507]"
        >
          Start with an assessment
          <ArrowRight
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      </motion.section>
    </main>
  );
};

export default Pricing;
